import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  AlertTriangle, 
  Clock, 
  TrendingDown, 
  Filter,
  Calendar,
  Download,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Loading } from '@/components/Loading';
import { slaService } from '@/services/slaService';
import { SLAMetricType, TicketPriority, type SLABreachAlert } from '@/types/sla';
import { buildBreachCsv } from '@/utils/sla-breach-export';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * SLA Breach Dashboard
 * Monitor and manage SLA breaches and tickets at risk
 */
export const SLABreachDashboard: React.FC = () => {
  useDocumentTitle('SLA Breach Dashboard | SupportDesk');
  const [breachedTickets, setBreachedTickets] = useState<SLABreachAlert[]>([]);
  const [approachingBreach, setApproachingBreach] = useState<SLABreachAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'breached' | 'approaching'>('breached');
  const [filters, setFilters] = useState({
    metricType: '' as SLAMetricType | '',
    priority: '' as TicketPriority | '',
    warningThreshold: 60
  });

  useEffect(() => {
    loadData();
  }, [filters.metricType, filters.priority, filters.warningThreshold]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [breached, approaching] = await Promise.all([
        slaService.getBreachedTickets(
          filters.metricType === '' ? undefined : filters.metricType,
          new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // Last 7 days
        ),
        slaService.getTicketsApproachingBreach(
          filters.warningThreshold,
          filters.metricType === '' ? undefined : filters.metricType
        )
      ]);

      // Apply priority filter if set
      const filteredBreached = filters.priority !== ''
        ? breached.filter(alert => alert.priority === filters.priority)
        : breached;
      
      const filteredApproaching = filters.priority !== ''
        ? approaching.filter(alert => alert.priority === filters.priority)
        : approaching;

      setBreachedTickets(filteredBreached);
      setApproachingBreach(filteredApproaching);
    } catch (err) {
      setError('Failed to load SLA breach data');
      setBreachedTickets([]);
      setApproachingBreach([]);
      console.error('Error loading breach data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: TicketPriority): string => {
    const colors: Record<string, string> = {
      'Critical': 'bg-red-100 text-red-800',
      'Urgent': 'bg-orange-100 text-orange-800',
      'High': 'bg-yellow-100 text-yellow-800',
      'Medium': 'bg-blue-100 text-blue-800',
      'Low': 'bg-green-100 text-green-800'
    };
    return colors[TicketPriority[priority]] || 'bg-gray-100 text-gray-800';
  };

  const getBreachType = (alert: SLABreachAlert): string => {
    return SLAMetricType[alert.metricType]?.replace(/([a-z])([A-Z])/g, '$1 $2') || 'Unknown';
  };

  const getTimeOverdue = (alert: SLABreachAlert): string => {
    if (alert.timeRemaining < 0) {
      const hoursOverdue = Math.ceil(Math.abs(alert.timeRemaining) / 60);
      return `${hoursOverdue}h overdue`;
    }
    return 'Not overdue';
  };

  const getTimeRemaining = (alert: SLABreachAlert): string => {
    if (alert.timeRemaining > 0) {
      if (alert.timeRemaining < 60) {
        return `${alert.timeRemaining}m remaining`;
      }
      const hours = Math.floor(alert.timeRemaining / 60);
      const minutes = alert.timeRemaining % 60;
      return `${hours}h ${minutes}m remaining`;
    }
    return 'Overdue';
  };

  const visibleAlerts = activeTab === 'breached' ? breachedTickets : approachingBreach;
  const exportData = () => {
    if (loading || error || visibleAlerts.length === 0) return;
    const url = URL.createObjectURL(new Blob(['\uFEFF', buildBreachCsv(visibleAlerts)], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `sla-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (loading && breachedTickets.length === 0 && approachingBreach.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return <Card className="p-6" role="alert">
      <h1 className="text-xl font-semibold">SLA Breach Dashboard</h1>
      <p className="my-4">{error}</p>
      <Button onClick={() => loadData()}>Try Again</Button>
    </Card>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SLA Breach Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Monitor tickets that have breached SLA or are at risk of breach
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            icon={<RefreshCw className="h-4 w-4" />}
            onClick={loadData}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outline"
            icon={<Download className="h-4 w-4" />}
            onClick={exportData}
            disabled={loading || !!error || visibleAlerts.length === 0}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">SLA Breaches</p>
              <p className="text-2xl font-bold text-gray-900">{breachedTickets.length}</p>
              <p className="text-sm text-red-600">Requires immediate attention</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">At Risk</p>
              <p className="text-2xl font-bold text-gray-900">{approachingBreach.length}</p>
              <p className="text-sm text-yellow-600">Within {filters.warningThreshold}min of breach</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingDown className="h-8 w-8 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Loaded Alerts</p>
              <p className="text-2xl font-bold text-gray-900">
                {breachedTickets.length + approachingBreach.length}
              </p>
              <p className="text-sm text-gray-600">Matching current filters</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <div className="flex items-center space-x-4 flex-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SLA Metric
              </label>
              <select
                value={filters.metricType}
                onChange={(e) => setFilters(prev => ({ ...prev, metricType: e.target.value === '' ? '' : Number(e.target.value) as SLAMetricType }))}
                className="block w-40 rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">All Metrics</option>
                <option value={SLAMetricType.FirstResponse}>First Response</option>
                <option value={SLAMetricType.Resolution}>Resolution</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value === '' ? '' : Number(e.target.value) as TicketPriority }))}
                className="block w-32 rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">All</option>
                <option value={TicketPriority.Critical}>Critical</option>
                <option value={TicketPriority.Urgent}>Urgent</option>
                <option value={TicketPriority.High}>High</option>
                <option value={TicketPriority.Medium}>Medium</option>
                <option value={TicketPriority.Low}>Low</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warning Threshold
              </label>
              <select
                value={filters.warningThreshold}
                onChange={(e) => setFilters(prev => ({ ...prev, warningThreshold: parseInt(e.target.value) }))}
                className="block w-32 rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={240}>4 hours</option>
              </select>
            </div>
          </div>

          {(filters.metricType !== '' || filters.priority !== '' || filters.warningThreshold !== 60) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ metricType: '', priority: '', warningThreshold: 60 })}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('breached')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'breached'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Breached ({breachedTickets.length})
          </button>
          <button
            onClick={() => setActiveTab('approaching')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'approaching'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            At Risk ({approachingBreach.length})
          </button>
        </nav>
      </div>

      {/* Tickets List */}
      <Card className="overflow-hidden">
        {activeTab === 'breached' ? (
          <div>
            {breachedTickets.length === 0 ? (
              <div className="p-12 text-center">
                <AlertTriangle className="h-16 w-16 text-green-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No SLA Breaches!</h3>
                <p className="text-gray-600">
                  Great job! No tickets have breached their SLA targets recently.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {breachedTickets.map((alert) => (
                  <div key={alert.ticketId} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Link
                            to={`/tickets/${alert.ticketId}`}
                            className="text-lg font-medium text-gray-900 hover:text-primary-600 flex items-center"
                          >
                            #{alert.ticketNumber} - {alert.title}
                            <ExternalLink className="h-4 w-4 ml-2" />
                          </Link>
                          <Badge className={getPriorityColor(alert.priority)}>
                            {TicketPriority[alert.priority] ?? 'Unknown'}
                          </Badge>
                          <Badge variant="error">
                            {getBreachType(alert)}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>Due {new Date(alert.dueDate).toLocaleDateString()}</span>
                          </div>
                          {alert.customerName && (
                            <div>
                              <span>Customer: {alert.customerName}</span>
                            </div>
                          )}
                          {alert.assignedAgentName && (
                            <div>
                              <span>Agent: {alert.assignedAgentName}</span>
                            </div>
                          )}
                          <div>
                            <span>Metric: {getBreachType(alert)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right ml-4">
                        <div className="text-lg font-semibold text-red-600">
                          {getTimeOverdue(alert)}
                        </div>
                        <div className="text-sm text-gray-500">
                          SLA breached
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {approachingBreach.length === 0 ? (
              <div className="p-12 text-center">
                <Clock className="h-16 w-16 text-green-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tickets At Risk!</h3>
                <p className="text-gray-600">
                  All tickets are well within their SLA targets.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {approachingBreach.map((alert) => (
                  <div key={alert.ticketId} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Link
                            to={`/tickets/${alert.ticketId}`}
                            className="text-lg font-medium text-gray-900 hover:text-primary-600 flex items-center"
                          >
                            #{alert.ticketNumber} - {alert.title}
                            <ExternalLink className="h-4 w-4 ml-2" />
                          </Link>
                          <Badge className={getPriorityColor(alert.priority)}>
                            {TicketPriority[alert.priority] ?? 'Unknown'}
                          </Badge>
                          <Badge variant="warning">
                            At Risk
                          </Badge>
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>Due {new Date(alert.dueDate).toLocaleDateString()}</span>
                          </div>
                          {alert.customerName && (
                            <div>
                              <span>Customer: {alert.customerName}</span>
                            </div>
                          )}
                          {alert.assignedAgentName && (
                            <div>
                              <span>Agent: {alert.assignedAgentName}</span>
                            </div>
                          )}
                          <div>
                            <span>Metric: {getBreachType(alert)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right ml-4">
                        <div className="text-lg font-semibold text-yellow-600">
                          {getTimeRemaining(alert)}
                        </div>
                        <div className="text-sm text-gray-500">
                          Until SLA breach
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};